import * as React from 'react';
import { useState, useEffect } from 'react';
import SendMessageModal from '../shared/SendMessageModal';
import { getPersonalMessages, getOrganizationMessages, IInboxItem } from '../../services/messages';
import moment from 'moment';
import { PropagateLoader, BeatLoader } from 'react-spinners';
import { FormattedMessage, defineMessages, injectIntl, InjectedIntlProps } from 'react-intl';
import { Link } from 'react-router-dom';
import { deleteMessage } from 'src/services/messages';
import { DASHBOARD_PATH, PARTNERS_PATH } from '../../constants';
import { IAlerts, IAccountDetails } from 'src/services/account';
import { get as getPartners } from '../../services/partner';

export default injectIntl(
    (
        props: InjectedIntlProps & {
            updateAlertCounts: IUpdateAlertCounts;
            accountDetails: IAccountDetails;
        }
    ) => {
        const [isLoadingMessages, setIsLoadingMessages] = useState(true);
        const [hasPartners, setHasPartners] = useState(true);
        const [messages, setMessages] = useState<IInboxItem[]>([]);
        const [outboxMessages, setOutboxMessages] = useState<IInboxItem[]>([]);
        const [deletingMessage, setDeletingMessage] = useState<string>('');

        const [inbox, setInbox] = useState<'inbox' | 'outbox'>('inbox');

        const i18nMessages = defineMessages({
            tableHdrName: {
                id: 'dash.msgs.table.hdr.name',
                defaultMessage: 'Name',
            },
            tableHdrFrom: {
                id: 'dash.msgs.table.hdr.from',
                defaultMessage: 'From',
            },
            tableHdrTo: {
                id: 'dash.msgs.table.hdr.to',
                defaultMessage: 'To',
            },
            tableHdrDb: {
                id: 'dash.msgs.table.hdr.db',
                defaultMessage: 'Database',
            },
            tableHdrSubject: {
                id: 'dash.msgs.table.hdr.subject',
                defaultMessage: 'Subject',
            },
            tableHdrDate: {
                id: 'dash.msgs.table.hdr.date',
                defaultMessage: 'Date',
            },
            tableHdrDatabase: {
                id: 'dash.msgs.table.hdr.database',
                defaultMessage: 'Database',
            },
            tableHdrDelete: {
                id: 'dash.msgs.table.hdr.delete',
                defaultMessage: 'Delete',
            },
            tableHdrType: {
                id: 'dash.msgs.table.hdr.type',
                defaultMessage: 'Type',
            },
            filterBy: {
                id: 'dash.msgs.filter.by',
                defaultMessage: 'Filter result by',
            },
            filters: {
                id: 'dash.msgs.filters',
                defaultMessage: 'Filters',
            },
            filterClear: {
                id: 'dash.msgs.filter.clear',
                defaultMessage: 'Clear filters',
            },
        });

        const updateHasPartners = async () => {
            const fetchedPartners = await getPartners();
            if (fetchedPartners.success) {
                setHasPartners(
                    !!(Array.isArray(fetchedPartners.partners) && fetchedPartners.partners.length)
                );
            } else setHasPartners(false);
        };

        const handleMessageDelete = async (m: IInboxItem) => {
            setDeletingMessage(m.name);
            const result = await deleteMessage(m.name);
            if (result.success) {
                setMessages(messages.filter((i: IInboxItem) => i.name !== m.name));
                setOutboxMessages(outboxMessages.filter((i: IInboxItem) => i.name !== m.name));
            }
            setDeletingMessage('');
        };

        const PARENS_CONTENT_REGEX = /\((.*?)\)/;

        const renderMessageRow = (m: IInboxItem) => {
            const date = moment(m.sent).calendar();

            const database = m.database && m.database.includes(" ")
                ? ((match => match ? match[1] : '')(m.database.match(PARENS_CONTENT_REGEX)))
                : '';

            const currentUserIsSender =
                m.from.includes(props.accountDetails.organization.orgName) ||
                m.from.includes(props.accountDetails.owner.email) ||
                m.from.includes(props.accountDetails.organization.email);
            const replyToDatabase = m.replyTo && !m.replyTo.includes("@") && PARENS_CONTENT_REGEX.test(m.replyTo);

            const name = replyToDatabase ? "All Partners" : ((currentUserIsSender ? m.to : m.from).match(PARENS_CONTENT_REGEX) || [,''])[1];

            return (
                <tr className="trcontent" key={`${m.sent}-${m.subject}`}>
                    <td>
                        {m.read || inbox === 'outbox' ? null : <span style={{ width: 10, height: 10, borderRadius: 10 }} className="bg-success m-2" />}
                        {name}
                    </td>
                    <td>
                        <Link to={`/client/dashboard/messages/${m.name}`}>{m.subject}</Link>
                    </td>
                    <td>{date}</td>
                    <td>{database}</td>
                    <td className="t-trash mx-auto">{renderMessageDelete(m)}</td>
                </tr>
            );
        };

        const renderMessageDelete = (m: IInboxItem) => {
            if (m.name === deletingMessage) {
                return <BeatLoader size={15} loading={true} color="#29447b" />;
            }
            return (
                <button className="icon-btn" onClick={() => handleMessageDelete(m)}>
                    <i className="fas fa-trash-alt" />
                </button>
            );
        };

        const renderMessageTable = (unread: boolean = true) => {
            if (isLoadingMessages) {
                return (
                    <div className="p-5 d-flex justify-content-center">
                        <PropagateLoader size={15} loading color="#29447b" />
                    </div>
                );
            }
            let messagesToRender: IInboxItem[] = [];
            if (inbox === 'outbox') {
                messagesToRender = outboxMessages;
            } else if (unread) {
                messagesToRender = messages.filter((m) => !m.read);
            } else {
                messagesToRender = messages.filter((m) => m.read);
            }
            if (messagesToRender.length === 0) {
                return (
                    <div className="text-center">
                        <i className="fas fa-inbox text-light-gray my-4" style={{ fontSize: 72 }} />
                        <br />
                        <span className="text-muted">
                            {unread ? <FormattedMessage
                                id="dash.msgs.unread.empty"
                                defaultMessage="You have no unread messages"
                            /> : <FormattedMessage
                                id="dash.msgs.empty"
                                defaultMessage="There are no messages here"
                            />}
                        </span>
                    </div>
                );
            }
            return (
                <table>
                    <thead>
                        <tr className="trhead">
                            <td>{(inbox === 'outbox') ? props.intl.formatMessage(i18nMessages.tableHdrTo) : props.intl.formatMessage(i18nMessages.tableHdrFrom)}</td>
                            <td>{props.intl.formatMessage(i18nMessages.tableHdrSubject)}</td>
                            <td>{props.intl.formatMessage(i18nMessages.tableHdrDate)}</td>
                            <td>{props.intl.formatMessage(i18nMessages.tableHdrDatabase)}</td>
                            <td className="t-trash">
                                {props.intl.formatMessage(i18nMessages.tableHdrDelete)}
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        {messagesToRender.map(renderMessageRow)}
                    </tbody>
                </table>
            );
        };

        const updateMail = async (type?: 'inbox' | 'outbox', forceShowLoader?: boolean) => {
            let isLoading = false
            if (!forceShowLoader && !inbox.length) {
                isLoading = true
                await setIsLoadingMessages(true);
            }
            const getMessages = getOrganizationMessages;
            const req = await getMessages();
            if (isLoading || isLoadingMessages) {
                await setIsLoadingMessages(false);
            }
            if (req.success && Array.isArray(req.inbox)) setMessages(req.inbox);
            if (req.success && Array.isArray(req.outbox)) setOutboxMessages(req.outbox);
            props.updateAlertCounts({ messages: (req.inbox || []).filter((m) => !m.read).length });
        };

        useEffect(() => {
            updateMail(inbox);
            updateHasPartners();
        }, []);

        const renderPageTabNav = () => {
            return (
                <div className="infotab col-lg-12">
                    <ul className="nav navtabs" id="myTab" role="tablist">
                        <li className="nav-item">
                            <a
                                onClick={async () => {
                                    setInbox('inbox');
                                    updateMail('inbox');
                                }}
                                className={
                                    inbox === 'inbox' ? 'nav-link active' : 'nav-link'
                                }
                                id="inbox-tab"
                                data-toggle="tab"
                                href="#inbox"
                                role="tab"
                                aria-controls="inbox"
                                aria-selected="true">
                                <FormattedMessage
                                    id="dash.msgs.tab.org"
                                    defaultMessage="Inbox"
                                />
                            </a>
                        </li>
                        <li className="nav-item">
                            <a
                                onClick={async () => {
                                    setInbox('outbox');
                                    updateMail('outbox');
                                }}
                                className={
                                    inbox === 'outbox'
                                        ? 'nav-link active'
                                        : 'nav-link'
                                }
                                id="outbox-tab"
                                data-toggle="tab"
                                href="#outbox"
                                role="tab"
                                aria-controls="outbox"
                                aria-selected="false">
                                <FormattedMessage id="dash.msgs.tab.outbox" defaultMessage="Sent" />
                            </a>
                        </li>
                    </ul>
                </div>
            );
        };

        if (!hasPartners && !messages.length) {
            return (
                <div className="col-12 my-5">
                    <Link to={PARTNERS_PATH}>
                        <i
                            className="fas fa-user-plus text-light-gray p-4 ml-auto d-block text-center"
                            style={{ fontSize: 100 }}
                        />
                    </Link>
                    <div className="d-flex justify-content-center">
                        <small className="text-muted">
                            You need to have partners to message. Invite partners to collaborate and
                            help build your database!
                        </small>
                    </div>
                </div>
            );
        }
        return (
            <div
                className="tab-pane show active mar-top-35 mobo-padding"
                id="Messages"
                role="tabpanel"
                aria-labelledby="profile-tab">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12 breadcrumb-col">
                            <ul>
                                <li>
                                    <Link to={DASHBOARD_PATH}>
                                        <FormattedMessage
                                            id="dash.msgs.bc.dashboard"
                                            defaultMessage="Dashboard"
                                        />
                                    </Link>
                                </li>
                                <li>
                                    <i className="fas fa-angle-right" />
                                </li>
                                <li>
                                    <FormattedMessage
                                        id="dash.msgs.bc.messages"
                                        defaultMessage="Messages"
                                    />
                                </li>
                            </ul>
                        </div>
                        <div className="col-lg-12 pagetitle">
                            <h2>
                                <FormattedMessage id="dash.msgs.hdr" defaultMessage="Messages" />
                            </h2>
                            <p className="text-muted font-weight-light">
                                <FormattedMessage
                                    id="dash.msgs.txt"
                                    defaultMessage="Send, receive and manage messages between your organization and your partners.  You can message any organization you are partnered with.  Click on the title to view a message."
                                />
                            </p>
                        </div>
                        <div className="col-sm-12 col-md-6 admintableCol">
                            <SendMessageModal
                                from={
                                    props.accountDetails
                                        ? props.accountDetails.organization.email
                                        : ''
                                }
                                onSave={updateMail}
                            />
                            {hasPartners || messages.length ? (
                                <button
                                    className="primary h3 px-3 py-2 m-0 mx-3"
                                    data-toggle="modal"
                                    data-target="#newMessageModal">
                                    <FormattedMessage
                                        id="dash.msgs.send.new"
                                        defaultMessage="Create New Message"
                                    />
                                </button>
                            ) : null}
                        </div>
                        {renderPageTabNav()}
                        <div className="col-lg-12 admintableCol messagehistory">
                            {inbox === 'inbox' &&
                                <>
                                    <FormattedMessage id="dash.msgs.new.hdr" defaultMessage="Unread Messages">
                                        {(txt: string) => (
                                            <h4 className="sub-title" style={{ display: 'inline' }}>
                                                {txt}
                                            </h4>
                                        )}
                                    </FormattedMessage>
                                    <div className="admintable">{renderMessageTable()}</div>
                                </>}
                            <FormattedMessage
                                id="dash.msgs.history.hdr"
                                defaultMessage="Message History">
                                {(txt: string) => <h4 className="sub-title">{txt}</h4>}
                            </FormattedMessage>
                            <p className="text-muted font-weight-light">
                                <FormattedMessage
                                    id="dash.msgs.history.help"
                                    defaultMessage="Below is a list of all archived messages sent to your organization.  To remove a message, click Delete."
                                />
                            </p>
                            <div className="admintable">{renderMessageTable(false)}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);



// WEBPACK FOOTER //
// ./src/client/dashboard/Messages.tsx